import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { CELL_TOOLTIP_CLASS } from "../Table/constants.js";
import { DIFF_MESSAGES_LENGTH, getCellValueIsEmpty } from "../../utils/cells.js";
import { named } from "../../utils/named.js";
import { v4 } from "/app/node_modules/.pnpm/uuid@9.0.0/node_modules/uuid/dist/esm-browser/index.js";
import { TooltipWrapper, TooltipInner, TooltipMessage, ValueRequiresDecision, ValueAdded, ValueRemovedMessage } from "./Styles.js";
const MESSAGE_LINE_HEIGHT = 16;
const MESSAGE_TOP_PADDING = 7;
const CONTAINER_BOTTOM_PADDING = 8;
const CHARACTERS_PER_LINE = 43;
const openUpwards = (messages, row, totalRows, rowHeight) => {
  const numberCellsBelow = totalRows - row - 1;
  const numberCellsAbove = row - 1;
  const heightOfCellsBelow = numberCellsBelow * rowHeight;
  const heightOfCellsAbove = numberCellsAbove * rowHeight;
  const tooltipMessagesHeight = messages.reduce((memo, message) => {
    const linesPerMessage = Math.ceil(message.length / CHARACTERS_PER_LINE);
    return memo + MESSAGE_TOP_PADDING + linesPerMessage * MESSAGE_LINE_HEIGHT;
  }, CONTAINER_BOTTOM_PADDING);
  const tooltipOverlapsBottom = tooltipMessagesHeight > heightOfCellsBelow;
  const tooltipOverlapsTop = tooltipMessagesHeight > heightOfCellsAbove;
  return tooltipOverlapsTop ? false : tooltipOverlapsBottom;
};
const formatDiffMessage = (message) => {
  const colon = message.indexOf(":") + 2;
  return [message.slice(0, colon), message.slice(colon)];
};
const getDiffMessages = (messages, cell) => {
  var _a, _b, _c;
  if (messages.length !== DIFF_MESSAGES_LENGTH)
    return null;
  const valueMessage = formatDiffMessage(messages[0]);
  const diffValueMessage = formatDiffMessage(messages[1]);
  const requiresDecision = ((_a = cell == null ? void 0 : cell.metadata) == null ? void 0 : _a.requiresDecision) && !((_b = cell == null ? void 0 : cell.metadata) == null ? void 0 : _b.acceptedValue);
  const acceptedValue = (_c = cell == null ? void 0 : cell.metadata) == null ? void 0 : _c.acceptedValue;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(TooltipMessage, { children: [
      valueMessage[0],
      requiresDecision && /* @__PURE__ */ jsx(ValueRequiresDecision, { $empty: getCellValueIsEmpty(cell == null ? void 0 : cell.diffValue), children: valueMessage[1] }),
      !requiresDecision && /* @__PURE__ */ jsx(Fragment, { children: acceptedValue === "source" ? /* @__PURE__ */ jsx(ValueAdded, { $empty: getCellValueIsEmpty(cell == null ? void 0 : cell.diffValue), children: valueMessage[1] }) : /* @__PURE__ */ jsx(ValueRemovedMessage, { $empty: getCellValueIsEmpty(cell == null ? void 0 : cell.value), children: valueMessage[1] }) })
    ] }, v4()),
    /* @__PURE__ */ jsxs(TooltipMessage, { children: [
      diffValueMessage[0],
      requiresDecision && /* @__PURE__ */ jsx(ValueRequiresDecision, { $empty: getCellValueIsEmpty(cell == null ? void 0 : cell.diffValue), children: diffValueMessage[1] }),
      !requiresDecision && /* @__PURE__ */ jsx(Fragment, { children: acceptedValue === "source" ? /* @__PURE__ */ jsx(
        ValueRemovedMessage,
        {
          $empty: getCellValueIsEmpty(cell == null ? void 0 : cell.diffValue),
          children: diffValueMessage[1]
        }
      ) : /* @__PURE__ */ jsx(ValueAdded, { $empty: getCellValueIsEmpty(cell == null ? void 0 : cell.value), children: diffValueMessage[1] }) })
    ] }, v4()),
    /* @__PURE__ */ jsx(TooltipMessage, { children: messages[2] }, v4())
  ] });
};
const Tooltip = ({
  status,
  row,
  cell,
  messages,
  metaMessages,
  diffMessages,
  totalRows,
  isLastRow,
  isFirstColumn,
  isLastColumn,
  isFirstNonHeaderRow,
  rowHeight
}) => {
  const metaMessage = metaMessages.join(", ");
  const shouldOpenUpwards = useMemo(() => {
    const totalMessages = metaMessage ? [...messages, metaMessage] : messages;
    return openUpwards(
      [...totalMessages, ...diffMessages],
      row,
      totalRows,
      rowHeight
    );
  }, [totalRows]);
  if (!messages.length && diffMessages.length !== DIFF_MESSAGES_LENGTH && !metaMessage) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    TooltipWrapper,
    {
      ...status,
      openUpwards: shouldOpenUpwards,
      isLastRow,
      isLastColumn,
      isFirstColumn,
      isFirstNonHeaderRow,
      className: CELL_TOOLTIP_CLASS,
      children: /* @__PURE__ */ jsxs(
        TooltipInner,
        {
          openUpwards: shouldOpenUpwards,
          isFirstNonHeaderRow,
          children: [
            messages.map((message) => /* @__PURE__ */ jsx(TooltipMessage, { children: message }, v4())),
            getDiffMessages(diffMessages, cell),
            !!metaMessage && /* @__PURE__ */ jsx(TooltipMessage, { meta: true, children: metaMessage }, v4())
          ]
        }
      )
    }
  );
};
named("Tooltip", Tooltip);
export {
  Tooltip as default
};
